/**
 * Two gates that sit in front of the whole FTUE rail.
 *
 * 1. READY — nothing may spotlight while the game is still coming up. The land
 *    paints before its data lands (profile, land status, mission), and a
 *    spotlight fired then points at a button that has not rendered or sits under
 *    a loading veil. Every rail asks this first.
 *
 * 2. CHOICE — the player is offered the guide on EVERY login for as long as
 *    day 1 is unfinished, so there is always a way out of it. The two answers
 *    are asymmetric on purpose:
 *      • "Guide me" lasts for the session — the next login asks again.
 *      • "Skip" is durable and account-wide, and it MARKS THE GUIDE AS SEEN so
 *        the one-time explainers can never be re-triggered later (a guide that
 *        could be replayed is a reward that could be farmed).
 *    Finishing day 1 closes the offer the same permanent way skipping does.
 */

import { useCallback, useSyncExternalStore } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '../store'

import { markExplanationSeenApi } from '../common/api'
import { RESOURCE_CHANGE_SUCCESS } from '../store/user/action-types'

/** The slices these gates read are not surfaced on RootState's inferred type,
 *  so widen it here — once — rather than casting at every selector. */
type GateRootState = RootState & {
  userModule?: {
    user?: { walletAddress?: string; seenExplanations?: string[]; nickName?: string }
  }
  app?: {
    game?: { isLoading?: boolean }
    land?: { buildingStatus?: Record<string, unknown> }
    mission?: {
      missionProgress?: unknown
      currentMission?: { missionDay?: number; quests?: { claimed?: boolean }[] }
      currentMissionData?: { missionDay?: number; quests?: unknown[] }
    }
  }
}

/** Answered the prompt (either way) — never ask again. */
const KEY_ASKED = 'ftueGuideAsked'
/** Answered "no" — the rail stays silent and the whole HUD is unlocked. */
const KEY_DECLINED = 'ftueGuideDeclined'
/** Day 1 was carried to its end — the guide has nothing left to say. */
const KEY_FINISHED = 'ftueGuideFinished'

/**
 * Every one-time explainer the rail owns. Declining stamps ALL of them, so
 * nothing can resurface on another device or after a support reset.
 *
 * Verified against the code: the seven strings below are exactly the keys
 * passed to useExplanationOnce() anywhere in src, plus the three gate flags.
 * Anything missing here would be a one-time explainer that survives a skip and
 * can be replayed for its reward, so re-check this list with
 * `grep -r "useExplanationOnce('" src` whenever an explainer is added.
 */
const ALL_GUIDE_KEYS = [
  KEY_ASKED,
  KEY_DECLINED,
  KEY_FINISHED,
  // NOT a useExplanationOnce() key: nothing reads 'landIconTour' anywhere in
  // src. FtueLandIconTour takes an `onDone` callback and is currently rendered
  // by nobody, so its owner (whoever mounts it) has to supply the once-only
  // gate. Kept here so that stamping stays correct if it is ever wired up to
  // this flag; drop it if the tour is deleted instead.
  'landIconTour',
  'craftShopIntro',
  'dailyQuestGuide',
  'rewardIconIntro',
  'generalVisitIntro',
  'palaceIntro',
  'pveMapIntro',
  'worldMap',
]

/** localStorage flag the in-battle card walkthrough uses. */
const BATTLE_TUTORIAL_KEY = 'eclipsera_battle_tutorial_completed'

/**
 * "Yes, guide me" holds only for the current session.
 *
 * The prompt is offered on EVERY login for as long as the guide is still owed,
 * so a player who wants out can leave at any point instead of being locked into
 * a story they accepted once, days ago. Taking the guide therefore must not be
 * durable — only SKIPPING is (that one is account-wide and permanent).
 *
 * Deliberately module state, not storage: it dies with the page, which is what
 * makes the next login ask again. sessionStorage would survive a reload and
 * silently swallow the prompt.
 */
let acceptedThisSession = false
const acceptListeners = new Set<() => void>()
function subscribeAccepted(cb: () => void) {
  acceptListeners.add(cb)
  return () => acceptListeners.delete(cb)
}
function markAcceptedThisSession() {
  acceptedThisSession = true
  acceptListeners.forEach((cb) => cb())
}
/** Test seam — the module flag would otherwise leak between cases. */
export function __setFtueSessionAccept(value: boolean) {
  acceptedThisSession = value
  acceptListeners.forEach((cb) => cb())
}

/**
 * True once the game is actually playable: wallet and nickname on the profile,
 * a mission progress row loaded, and the global loading veil gone. While false,
 * no FTUE step may appear.
 *
 * Note it does NOT require land building status, despite reading it — see the
 * comment on `void buildingStatus` below for why.
 */
export function useFtueReady(): boolean {
  const walletAddress = useSelector((s: RootState) => (s as GateRootState).userModule?.user?.walletAddress)
  const gameLoading = useSelector((s: RootState) => !!(s as GateRootState).app?.game?.isLoading)
  const buildingStatus = useSelector((s: RootState) => (s as GateRootState).app?.land?.buildingStatus)
  const missionProgress = useSelector((s: RootState) => (s as GateRootState).app?.mission?.missionProgress)
  const nickName = useSelector((s: RootState) => (s as GateRootState).userModule?.user?.nickName)

  // NOTE: land building status is deliberately NOT required. It is only ever
  // populated on the land route, so requiring it silenced every rail on the
  // map, character-select and battle screens. Each land rail anchors to a
  // building element anyway, and a spotlight renders nothing until its anchor
  // exists — so the land gates itself.
  void buildingStatus
  // The naming step runs OVER the land, where the rail is already mounted, so
  // the guide must wait for a nickname or it interrupts signup.
  //
  // Being on another route is NOT enough on its own: MissionPack sits inside
  // App's PersistentLandPage, which stays MOUNTED once "/" has been visited and
  // only hides itself with `display: none`. Anything that portals to
  // document.body therefore escapes that hiding and paints over /start and
  // /select-main-character, so each such component excludes routes itself
  // (FtueGuidePrompt and FtueSkipButton both do).
  //
  // Deliberately NOT gated on the roster: nothing fetches characters on the
  // land, so requiring one left `ready` false forever and silenced the entire
  // guide after login.
  return !!walletAddress && !!nickName && !gameLoading && !!missionProgress
}

export interface FtueGuideChoice {
  /** Day 1 is still unfinished and the player has not taken the guide this
   *  session, skipped it, or completed it — so the offer is owed right now. */
  shouldAsk: boolean
  /** The prompt is on screen, so the rail must hold until it is answered. */
  blocking: boolean
  /** The player said no — rail silent, whole HUD unlocked. */
  declined: boolean
  /** Day 1 is done (or was skipped): the guide never speaks again. */
  over: boolean
  /** Close the guide for good after the final mission. */
  finish: () => void
  /** Take the guide (records the answer only). */
  accept: () => void
  /** Skip the guide: records it AND stamps every one-time explainer as seen. */
  decline: () => void
}

export function useFtueGuideChoice(): FtueGuideChoice {
  const dispatch = useDispatch()
  const walletAddress = useSelector((s: RootState) => (s as GateRootState).userModule?.user?.walletAddress)
  const seen = useSelector((s: RootState) => (s as GateRootState).userModule?.user?.seenExplanations)
  const seenList = seen ?? []
  const missionProgress = useSelector(
    (s: RootState) =>
      (s as GateRootState).app?.mission?.missionProgress as
      | { missionDay?: number; questId?: number }
      | undefined,
  )
  const currentMission = useSelector(
    (s: RootState) => (s as GateRootState).app?.mission?.currentMission,
  )

  /**
   * Who is a first-time player is decided by the FTUE QUEUE, never by whether
   * the intro video was watched: a newcomer who has already played the video
   * still has the whole day-1 story ahead of them, while a veteran has seen it
   * long ago with nothing left in the queue. Judging by the video marked real
   * newcomers as veterans the moment they pressed play.
   */
  const currentMissionData = useSelector(
    (s: RootState) => (s as GateRootState).app?.mission?.currentMissionData,
  )
  const dayOneQuests =
    currentMission?.missionDay === 1 ? currentMission?.quests ?? null : null
  // Same trap as useFtueDayOneActive: the row only holds STARTED quests, so an
  // all-claimed row does not mean the day is done.
  const dayOneTotal =
    currentMissionData?.missionDay === 1 ? currentMissionData?.quests?.length ?? 0 : 0
  const dayOneDone =
    dayOneTotal > 0 &&
    (dayOneQuests?.length ?? 0) >= dayOneTotal &&
    !!dayOneQuests?.every((q) => q.claimed)
  const ftueQueuePending = missionProgress?.missionDay === 1 && !dayOneDone

  /** Mirror locally first so the UI reacts instantly, then persist. */
  const stamp = useCallback(
    (keys: string[]) => {
      if (!walletAddress) return
      dispatch({
        type: RESOURCE_CHANGE_SUCCESS,
        payload: { data: { seenExplanations: Array.from(new Set([...seenList, ...keys])) } },
      })
      for (const key of keys) {
        void markExplanationSeenApi(walletAddress, key).catch(() => { })
      }
    },
    [dispatch, walletAddress, seenList],
  )

  /** Records that the offer was made (audit only — it no longer silences the
   *  prompt) and lets the rail run for the rest of this session. */
  const accept = useCallback(() => {
    markAcceptedThisSession()
    stamp([KEY_ASKED])
  }, [stamp])

  /** The story reached its end: stamp everything, exactly as declining does,
   * so no explainer can resurface on another device or after a reset. */
  const finish = useCallback(() => {
    stamp(ALL_GUIDE_KEYS.filter((k) => k !== KEY_DECLINED && k !== KEY_ASKED))
    try {
      localStorage.setItem(BATTLE_TUTORIAL_KEY, 'true')
    } catch {
      /* private mode */
    }
  }, [stamp])

  const decline = useCallback(() => {
    // Anti-farm: a declined guide counts as a SEEN guide, so none of its
    // one-time explainers can be shown (or rewarded) again later.
    //
    // KEY_ASKED is deliberately excluded. Marking it seen buys nothing (nothing
    // gates on it any more), but posting it would re-fire the server's
    // park-the-board hook alongside this decline's hand-back hook, unordered —
    // and a park that lands last leaves the player's buildings empty with the
    // guide already over, i.e. no way to ask for them back.
    stamp(ALL_GUIDE_KEYS.filter((k) => k !== KEY_ASKED))
    try {
      localStorage.setItem(BATTLE_TUTORIAL_KEY, 'true')
    } catch {
      /* private mode — the battle walkthrough may still appear once */
    }
  }, [stamp])

  const declined = seenList.includes(KEY_DECLINED)
  const finished = seenList.includes(KEY_FINISHED)
  const accepted = useSyncExternalStore(
    subscribeAccepted,
    () => acceptedThisSession,
    () => false,
  )
  /**
   * Owed on every login while day 1 is unfinished — NOT only at quest 1. A
   * player who logs back in halfway through is asked again, which is the only
   * way out of a guide they no longer want. Skipping and finishing are both
   * permanent, so neither can be re-prompted.
   */
  const askOwed = !!walletAddress && ftueQueuePending && !declined && !finished && !accepted
  return {
    shouldAsk: askOwed,
    blocking: askOwed,
    declined,
    over: declined || finished,
    finish,
    accept,
    decline,
  }
}
